import { Action } from "redux";

import { FacetsData } from "@/mk2/apps/bazaar/components/FacetsFilter";
import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Filter } from "@/mk/bazaar/product/filter/filterUtils";

export interface Checked {
    [productId: number]: true;
}

export interface BazaarGridLoadApiAction extends Action {
    readonly filter: Filter;
    readonly watchdogKey: string;
}

export const BAZAAR_GRID_LOAD_API_REQUEST = "BAZAAR_GRID_LOAD_API_REQUEST";

export interface BazaarGridLoadApiRequestAction extends BazaarGridLoadApiAction, APIRequestAction {
    readonly type: typeof BAZAAR_GRID_LOAD_API_REQUEST;
}

export interface BazaarGridLoadNormalizedResponse extends NormalizedResponse {
    readonly result: {
        products: number[];
        complaints: {
            [productId: number]: number[];
        };
        strollerPhotos: number[];
    };
}

export const BAZAAR_GRID_LOAD_API_SUCCESS = "BAZAAR_GRID_LOAD_API_SUCCESS";

export interface BazaarGridLoadApiSuccessAction
    extends BazaarGridLoadApiAction, APISuccessAction<BazaarGridLoadNormalizedResponse> {
    readonly type: typeof BAZAAR_GRID_LOAD_API_SUCCESS;
    readonly facets: FacetsData;
    readonly hits: number;
    readonly hitsAll: number;
    readonly hitsLiked: number;
    readonly checked: Checked;
    readonly watchdog: boolean;
    readonly strollersIdsOrdered: number[];
}

export const BAZAAR_GRID_LOAD_API_FAILURE = "BAZAAR_GRID_LOAD_API_FAILURE";

export interface BazaarGridLoadApiFailureAction extends BazaarGridLoadApiAction, APIFailureAction {
    readonly type: typeof BAZAAR_GRID_LOAD_API_FAILURE;
}

export const bazaarGridLoadApi = {
    request: (filter: Filter, watchdogKey: string): BazaarGridLoadApiRequestAction => ({
        type: BAZAAR_GRID_LOAD_API_REQUEST,
        filter,
        watchdogKey,
    }),
    success: (
        filter: Filter,
        watchdogKey: string,
        facets: FacetsData,
        hits: number,
        hitsAll: number,
        hitsLiked: number,
        checked: Checked,
        watchdog: boolean,
        strollersIdsOrdered: number[],
        response: BazaarGridLoadNormalizedResponse,
    ): BazaarGridLoadApiSuccessAction => ({
        type: BAZAAR_GRID_LOAD_API_SUCCESS,
        filter,
        watchdogKey,
        facets,
        hits,
        hitsAll,
        hitsLiked,
        checked,
        watchdog,
        strollersIdsOrdered,
        response,
    }),
    failure: (
        filter: Filter,
        watchdogKey: string,
        error: string,
    ): BazaarGridLoadApiFailureAction => ({
        type: BAZAAR_GRID_LOAD_API_FAILURE,
        filter,
        watchdogKey,
        error,
    }),
};

export const BAZAAR_GRID_LOAD = "BAZAAR_GRID_LOAD";

export interface BazaarGridLoadAction extends BazaarGridLoadApiAction {
    readonly type: typeof BAZAAR_GRID_LOAD;
    readonly location: HistoryLocationWithState;
}

export const bazaarGridLoad = (
    filter: Filter,
    watchdogKey: string,
    location: HistoryLocationWithState,
): BazaarGridLoadAction => ({
    type: BAZAAR_GRID_LOAD,
    filter,
    watchdogKey,
    location,
});

export interface BazaarGridWatchdogSubscribeApiAction extends Action {
    readonly filter: Filter;
    readonly watchdogKey: string;
}

export const BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_REQUEST =
    "BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_REQUEST";

export interface BazaarGridWatchdogSubscribeApiRequestAction
    extends BazaarGridWatchdogSubscribeApiAction, APIRequestAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_REQUEST;
}

export interface BazaarGridWatchdogSubscribeApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_SUCCESS =
    "BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_SUCCESS";

export interface BazaarGridWatchdogSubscribeApiSuccessAction
    extends
        BazaarGridWatchdogSubscribeApiAction,
        APISuccessAction<BazaarGridWatchdogSubscribeApiNormalizedResponse> {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_SUCCESS;
}

export const BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE =
    "BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE";

export interface BazaarGridWatchdogSubscribeApiFailureAction
    extends BazaarGridWatchdogSubscribeApiAction, APIFailureAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE;
}

export const bazaarGridWatchdogSubscribeApi = {
    request: (
        filter: Filter,
        watchdogKey: string,
    ): BazaarGridWatchdogSubscribeApiRequestAction => ({
        type: BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_REQUEST,
        filter,
        watchdogKey,
    }),
    success: (
        filter: Filter,
        watchdogKey: string,
        response: BazaarGridWatchdogSubscribeApiNormalizedResponse,
    ): BazaarGridWatchdogSubscribeApiSuccessAction => ({
        type: BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_SUCCESS,
        filter,
        watchdogKey,
        response,
    }),
    failure: (
        filter: Filter,
        watchdogKey: string,
        error: string,
    ): BazaarGridWatchdogSubscribeApiFailureAction => ({
        type: BAZAAR_GRID_WATCHDOG_SUBSCRIBE_API_FAILURE,
        filter,
        watchdogKey,
        error,
    }),
};

export const BAZAAR_GRID_WATCHDOG_SUBSCRIBE = "BAZAAR_GRID_WATCHDOG_SUBSCRIBE";

export interface BazaarGridWatchdogSubscribeAction extends BazaarGridWatchdogSubscribeApiAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_SUBSCRIBE;
    readonly location: HistoryLocationWithState;
}

export const bazaarGridWatchdogSubscribe = (
    filter: Filter,
    watchdogKey: string,
    location: HistoryLocationWithState,
): BazaarGridWatchdogSubscribeAction => ({
    type: BAZAAR_GRID_WATCHDOG_SUBSCRIBE,
    filter,
    watchdogKey,
    location,
});

export interface BazaarGridWatchdogUnsubscribeApiAction extends Action {
    readonly filter: Filter;
    readonly watchdogKey: string;
}

export const BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_REQUEST =
    "BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_REQUEST";

export interface BazaarGridWatchdogUnsubscribeApiRequestAction
    extends BazaarGridWatchdogUnsubscribeApiAction, APIRequestAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_REQUEST;
}

export interface BazaarGridWatchdogUnsubscribeApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_SUCCESS =
    "BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_SUCCESS";

export interface BazaarGridWatchdogUnsubscribeApiSuccessAction
    extends
        BazaarGridWatchdogUnsubscribeApiAction,
        APISuccessAction<BazaarGridWatchdogUnsubscribeApiNormalizedResponse> {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_SUCCESS;
}

export const BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE =
    "BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE";

export interface BazaarGridWatchdogUnsubscribeApiFailureAction
    extends BazaarGridWatchdogUnsubscribeApiAction, APIFailureAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE;
}

export const bazaarGridWatchdogUnsubscribeApi = {
    request: (
        filter: Filter,
        watchdogKey: string,
    ): BazaarGridWatchdogUnsubscribeApiRequestAction => ({
        type: BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_REQUEST,
        filter,
        watchdogKey,
    }),
    success: (
        filter: Filter,
        watchdogKey: string,
        response: BazaarGridWatchdogUnsubscribeApiNormalizedResponse,
    ): BazaarGridWatchdogUnsubscribeApiSuccessAction => ({
        type: BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_SUCCESS,
        filter,
        watchdogKey,
        response,
    }),
    failure: (
        filter: Filter,
        watchdogKey: string,
        error: string,
    ): BazaarGridWatchdogUnsubscribeApiFailureAction => ({
        type: BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE_API_FAILURE,
        filter,
        watchdogKey,
        error,
    }),
};

export const BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE = "BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE";

export interface BazaarGridWatchdogUnsubscribeAction extends BazaarGridWatchdogUnsubscribeApiAction {
    readonly type: typeof BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE;
    readonly location: HistoryLocationWithState;
}

export const bazaarGridWatchdogUnsubscribe = (
    filter: Filter,
    watchdogKey: string,
    location: HistoryLocationWithState,
): BazaarGridWatchdogUnsubscribeAction => ({
    type: BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE,
    filter,
    watchdogKey,
    location,
});
